import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ============================================================================
// INTERFACES POUR LES PÔLES
// ============================================================================

export interface Pole {
  id: number;
  code: string;
  nom: string;
  description?: string;
  ordre_affichage: number;
  actif: boolean;
  nb_options?: number;
  date_creation?: string;
  date_modification?: string;
}

export interface PoleList {
  id: number;
  code: string;
  nom: string;
  description?: string;
  actif: boolean;
}

export interface PoleCreate {
  code: string;
  nom: string;
  description?: string;
  ordre_affichage?: number;
  actif?: boolean;
}

// ============================================================================
// INTERFACES POUR LES PARCOURS
// ============================================================================

export interface Parcours {
  id: number;
  code: string;
  nom: string;
  pole: number;
  pole_nom: string;
  pole_code: string;
  description?: string;
  ordre_affichage: number;
  actif: boolean;
  nb_options?: number;
  date_creation?: string;
  date_modification?: string;
}

export interface ParcoursList {
  id: number;
  code: string;
  nom: string;
  pole: number;
  pole_nom: string;
  description?: string;
  actif: boolean;
}

export interface ParcoursCreate {
  code: string;
  nom: string;
  pole: number;
  description?: string;
  ordre_affichage?: number;
  actif?: boolean;
}

export interface ParcoursParPole {
  pole: PoleList;
  parcours: ParcoursList[];
}

// ============================================================================
// INTERFACES POUR LES OPTIONS
// ============================================================================

export interface Option {
  id: number;
  code: string;
  nom: string;
  pole: number;
  pole_nom: string;
  pole_code: string;
  parcours?: number | null;
  parcours_nom?: string | null;
  description?: string;
  actif: boolean;
  exclue_edt_examens?: boolean;
  date_creation?: string;
  date_modification?: string;
}

export interface OptionList {
  id: number;
  code: string;
  nom: string;
  pole: number;
  pole_nom: string;
  parcours?: number | null;
  parcours_nom?: string | null;
  description?: string;
  actif: boolean;
  exclue_edt_examens?: boolean;
}

export interface OptionCreate {
  code: string;
  nom: string;
  pole: number;
  parcours?: number | null;
  description?: string;
  actif?: boolean;
  exclue_edt_examens?: boolean;
}

export interface OptionsParPole {
  pole: PoleList;
  options: OptionList[];
}

// ============================================================================
// INTERFACES POUR LES CLASSES
// ============================================================================

export interface Classe {
  id: number;
  niveau: string;
  niveau_display: string;
  option: number;
  option_nom: string;
  option_code: string;
  pole_nom: string;
  parcours_nom?: string | null;
  code: string;
  annee_academique?: number | null;
  annee_academique_libelle?: string | null;
  effectif_max: number;
  actif: boolean;
  date_creation?: string;
  date_modification?: string;
}

export interface ClasseList {
  id: number;
  code: string;
  niveau: string;
  option_nom: string;
  option_id: number;
  pole_nom: string;
  parcours_nom: string | null;
  actif: boolean;
}

export interface ClasseCreate {
  niveau: string;
  option: number;
  annee_academique?: number | null;
  effectif_max?: number;
  actif?: boolean;
}

export interface ClassesParNiveau {
  niveau: string;
  niveau_display: string;
  classes: ClasseList[];
  total: number;
}

// ============================================================================
// INTERFACES POUR LA DÉTECTION DE DOUBLONS
// ============================================================================

export interface DoublonEntry {
  name: string;
  id: number;
  similarity: number;
  nb_etudiants: number;
}

export interface DoublonGroup {
  canonical: string;
  canonical_id: number;
  nb_etudiants: number;
  duplicates: DoublonEntry[];
}

export interface DoublonsWarning {
  options: DoublonGroup[];
  parcours: DoublonGroup[];
}

export interface SyncResponse {
  success: boolean;
  message: string;
  stats: {
    poles_crees: number;
    parcours_crees: number;
    options_crees: number;
    classes_crees: number;
  };
  doublons?: DoublonsWarning;
}

export interface MergeResponse {
  success: boolean;
  message: string;
  etudiants_modifies: number;
  [key: string]: any;
}

// ============================================================================
// API SERVICE
// ============================================================================

export const polesOptionsApi = createApi({
  reducerPath: 'polesOptionsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Poles', 'Parcours', 'Options', 'Classes'],
  endpoints: (builder) => ({
    // ========== PÔLES ==========

    // Liste des pôles
    getPoles: builder.query<PoleList[], { actif?: boolean } | void>({
      query: (params) => ({
        url: '/poles/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Poles'],
    }),

    // Détail d'un pôle
    getPole: builder.query<Pole, number>({
      query: (id) => `/poles/${id}/`,
      providesTags: ['Poles'],
    }),

    // Créer un pôle
    createPole: builder.mutation<Pole, PoleCreate>({
      query: (data) => ({
        url: '/poles/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Poles'],
    }),

    // Modifier un pôle
    updatePole: builder.mutation<Pole, { id: number; data: Partial<PoleCreate> }>({
      query: ({ id, data }) => ({
        url: `/poles/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Poles'],
    }),

    // Supprimer un pôle
    deletePole: builder.mutation<void, number>({
      query: (id) => ({
        url: `/poles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Poles'],
    }),

    // Options d'un pôle
    getPoleOptions: builder.query<OptionList[], number>({
      query: (id) => `/poles/${id}/options/`,
      providesTags: ['Options'],
    }),

    // Parcours d'un pôle
    getPoleParcours: builder.query<ParcoursList[], number>({
      query: (id) => `/poles/${id}/parcours/`,
      providesTags: ['Parcours'],
    }),

    // Vérifier les doublons
    checkDoublons: builder.query<DoublonsWarning, void>({
      query: () => '/poles/check-doublons/',
      providesTags: ['Options', 'Parcours'],
    }),

    // Synchroniser depuis les étudiants
    syncFromEtudiants: builder.mutation<SyncResponse, void>({
      query: () => ({
        url: '/poles/sync-etudiants/',
        method: 'POST',
      }),
      invalidatesTags: ['Poles', 'Parcours', 'Options', 'Classes'],
    }),

    // Fusionner des options doublons
    mergeOptions: builder.mutation<MergeResponse, { canonicalId: number; merge_ids: number[] }>({
      query: ({ canonicalId, merge_ids }) => ({
        url: `/options/${canonicalId}/merge/`,
        method: 'POST',
        body: { merge_ids },
      }),
      invalidatesTags: ['Options', 'Classes'],
    }),

    // Fusionner des parcours doublons
    mergeParcours: builder.mutation<MergeResponse, { canonicalId: number; merge_ids: number[] }>({
      query: ({ canonicalId, merge_ids }) => ({
        url: `/parcours/${canonicalId}/merge/`,
        method: 'POST',
        body: { merge_ids },
      }),
      invalidatesTags: ['Parcours', 'Options'],
    }),

    // ========== PARCOURS ==========

    // Liste des parcours
    getParcoursList: builder.query<ParcoursList[], { actif?: boolean; pole?: number } | void>({
      query: (params) => ({
        url: '/parcours/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Parcours'],
    }),

    // Détail d'un parcours
    getParcours: builder.query<Parcours, number>({
      query: (id) => `/parcours/${id}/`,
      providesTags: ['Parcours'],
    }),

    // Créer un parcours
    createParcours: builder.mutation<Parcours, ParcoursCreate>({
      query: (data) => ({
        url: '/parcours/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Parcours', 'Poles'],
    }),

    // Modifier un parcours
    updateParcours: builder.mutation<Parcours, { id: number; data: Partial<ParcoursCreate> }>({
      query: ({ id, data }) => ({
        url: `/parcours/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Parcours', 'Poles'],
    }),

    // Supprimer un parcours
    deleteParcours: builder.mutation<void, number>({
      query: (id) => ({
        url: `/parcours/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Parcours', 'Poles', 'Options'],
    }),

    // Parcours groupés par pôle
    getParcoursParPole: builder.query<ParcoursParPole[], void>({
      query: () => '/parcours/par_pole/',
      providesTags: ['Parcours', 'Poles'],
    }),

    // Options d'un parcours
    getParcoursOptions: builder.query<OptionList[], number>({
      query: (id) => `/parcours/${id}/options/`,
      providesTags: ['Options'],
    }),

    // ========== OPTIONS ==========

    // Liste des options
    getOptions: builder.query<OptionList[], { actif?: boolean; pole?: number; parcours?: number } | void>({
      query: (params) => ({
        url: '/options/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Options'],
    }),

    // Détail d'une option
    getOption: builder.query<Option, number>({
      query: (id) => `/options/${id}/`,
      providesTags: ['Options'],
    }),

    // Créer une option
    createOption: builder.mutation<Option, OptionCreate>({
      query: (data) => ({
        url: '/options/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Options', 'Poles'],
    }),

    // Modifier une option
    updateOption: builder.mutation<Option, { id: number; data: Partial<OptionCreate> }>({
      query: ({ id, data }) => ({
        url: `/options/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Options', 'Poles'],
    }),

    // Supprimer une option
    deleteOption: builder.mutation<void, number>({
      query: (id) => ({
        url: `/options/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Options', 'Poles'],
    }),

    // Options groupées par pôle
    getOptionsParPole: builder.query<OptionsParPole[], void>({
      query: () => '/options/par_pole/',
      providesTags: ['Options', 'Poles'],
    }),

    // ========== CLASSES ==========

    // Liste des classes
    getClasses: builder.query<ClasseList[], {
      actif?: boolean;
      niveau?: string;
      option?: number;
      pole?: number;
      annee_academique?: number;
    } | void>({
      query: (params) => ({
        url: '/classes/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Classes'],
    }),

    // Détail d'une classe
    getClasse: builder.query<Classe, number>({
      query: (id) => `/classes/${id}/`,
      providesTags: ['Classes'],
    }),

    // Créer une classe
    createClasse: builder.mutation<Classe, ClasseCreate>({
      query: (data) => ({
        url: '/classes/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Classes'],
    }),

    // Modifier une classe
    updateClasse: builder.mutation<Classe, { id: number; data: Partial<ClasseCreate> }>({
      query: ({ id, data }) => ({
        url: `/classes/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Classes'],
    }),

    // Supprimer une classe
    deleteClasse: builder.mutation<void, number>({
      query: (id) => ({
        url: `/classes/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Classes'],
    }),

    // Classes groupées par niveau
    getClassesParNiveau: builder.query<ClassesParNiveau[], void>({
      query: () => '/classes/par_niveau/',
      providesTags: ['Classes'],
    }),

    // Copier les classes d'un niveau vers un autre
    copierNiveau: builder.mutation<{
      success: boolean;
      message: string;
      classes: ClasseList[];
    }, {
      niveau_source: string;
      niveau_destination: string;
      annee_academique?: number | null;
    }>({
      query: (data) => ({
        url: '/classes/copier_niveau/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Classes'],
    }),

    // Purge structurelle : supprime Classes → Options → Parcours → Pôles.
    // Confirmation textuelle obligatoire (endpoint refuse sinon).
    purgerStructures: builder.mutation<{
      success: boolean;
      nb_classes: number;
      nb_options: number;
      nb_parcours: number;
      nb_poles: number;
      message: string;
    }, { confirmation: string }>({
      query: (data) => ({
        url: '/gestion-options/purger-structures/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Poles', 'Parcours', 'Options', 'Classes'],
    }),
  }),
});

export const {
  // Pôles
  useGetPolesQuery,
  useGetPoleQuery,
  useCreatePoleMutation,
  useUpdatePoleMutation,
  useDeletePoleMutation,
  useGetPoleOptionsQuery,
  useGetPoleParcoursQuery,
  useCheckDoublonsQuery,
  useSyncFromEtudiantsMutation,
  useMergeOptionsMutation,
  useMergeParcoursMutation,
  // Parcours
  useGetParcoursListQuery,
  useGetParcoursQuery,
  useCreateParcoursMutation,
  useUpdateParcoursMutation,
  useDeleteParcoursMutation,
  useGetParcoursParPoleQuery,
  useGetParcoursOptionsQuery,
  // Options
  useGetOptionsQuery,
  useGetOptionQuery,
  useCreateOptionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
  useGetOptionsParPoleQuery,
  // Classes
  useGetClassesQuery,
  useGetClasseQuery,
  useCreateClasseMutation,
  useUpdateClasseMutation,
  useDeleteClasseMutation,
  useGetClassesParNiveauQuery,
  useCopierNiveauMutation,
  // Purge structurelle
  usePurgerStructuresMutation,
} = polesOptionsApi;
